import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import ThemeToggle from '../components/ui/ThemeToggle'
import FeatureSuggestions from '../components/FeatureSuggestions'
import MarketingTools from '../components/MarketingTools'
import { 
  Shield, Users, CreditCard, FolderOpen, AlertTriangle, MessageSquare, 
  Activity, TrendingUp, Clock, ChevronDown, ChevronUp, 
  ArrowLeft, RefreshCw, Search, Lightbulb, Megaphone, Share2
} from 'lucide-react'

const API_URL = import.meta.env.VITE_API_URL || ''
const ADMIN_EMAILS = [
  'starrcustomclothing@gmail.com',
  'eclecticserenitybystarr@gmail.com',
  'ellamekayne2@gmail.com',
  'utopiasedge@gmail.com',
  'akrareese@gmail.com',
]
const isAdminEmail = (e) => !!e && ADMIN_EMAILS.includes(e.toLowerCase().trim())

export default function Admin() {
  const navigate = useNavigate()
  const { user } = useAuth()
  
  const [stats, setStats] = useState(null)
  const [complaints, setComplaints] = useState([])
  const [complaintsTotal, setComplaintsTotal] = useState(0)
  const [complaintsPage, setComplaintsPage] = useState(1)
  const [complaintFilter, setComplaintFilter] = useState('')
  const [activeUsers, setActiveUsers] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [expandedComplaint, setExpandedComplaint] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [userSearch, setUserSearch] = useState('')

  useEffect(() => {
    if (!isAdminEmail(user?.email)) {
      navigate('/')
    }
  }, [user, navigate])

  const fetchData = useCallback(async () => {
    if (!isAdminEmail(user?.email)) return
    
    try {
      const [statsRes, complaintsRes, activityRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/admin/stats?email=${encodeURIComponent(user.email)}`),
        fetch(`${API_URL}/admin/complaints?email=${encodeURIComponent(user.email)}&page=${complaintsPage}${complaintFilter ? `&status=${complaintFilter}` : ''}`),
        fetch(`${API_URL}/admin/activity?email=${encodeURIComponent(user.email)}`),
        fetch(`${API_URL}/admin/users?email=${encodeURIComponent(user.email)}`)
      ])

      const [statsData, complaintsData, activityData, usersData] = await Promise.all([
        statsRes.json(),
        complaintsRes.json(),
        activityRes.json(),
        usersRes.json()
      ])

      setStats(statsData)
      setComplaints(complaintsData.complaints || [])
      setComplaintsTotal(complaintsData.total || 0)
      setActiveUsers(activityData.active_users || [])
      setAllUsers(usersData.users || [])
      setLoading(false)
    } catch (err) {
      console.error('Admin fetch error:', err)
      setLoading(false)
    }
  }, [user, complaintsPage, complaintFilter])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchData()
    setTimeout(() => setRefreshing(false), 500)
  }

  const updateComplaint = async (id, status, notes) => {
    try {
      await fetch(`${API_URL}/admin/complaints/${id}?email=${encodeURIComponent(user.email)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, status, admin_notes: notes })
      })
      fetchData()
    } catch (err) {
      console.error('Update complaint error:', err)
    }
  }

  if (!isAdminEmail(user?.email)) return null
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-zenna-violet/30 border-t-zenna-violet rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Loading admin dashboard...</p>
        </div>
      </div>
    )
  }

  const severityColor = (s) => ({
    low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  })[s] || 'bg-slate-100 text-slate-600'

  const statusColor = (s) => ({
    new: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    investigating: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    dismissed: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
  })[s] || 'bg-slate-100 text-slate-600'

  const filteredUsers = allUsers.filter(u => 
    !userSearch || 
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(userSearch.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-zenna-violet" />
              <h1 className="text-xl font-bold text-slate-800 dark:text-white">Admin Dashboard</h1>
            </div>
            <span className="text-xs bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded-full font-medium">
              ADMIN ONLY
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handleRefresh}
              className={`p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg ${refreshing ? 'animate-spin' : ''}`}
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5 text-slate-500" />
            </button>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                {stats?.active_users_now || 0} online now
              </span>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs with Marketing */}
        <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-slate-800 rounded-xl p-1 border border-slate-200 dark:border-slate-700 w-fit">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'marketing', label: '🚀 Marketing', icon: Megaphone },
            { id: 'social', label: 'Social Media', icon: Share2 },
            { id: 'suggestions', label: 'Feature Ideas', icon: Lightbulb },
            { id: 'complaints', label: `Complaints (${stats?.new_complaints || 0})`, icon: AlertTriangle },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'activity', label: 'Live Activity', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-zenna-violet text-white'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                { label: 'Paying Users', value: stats?.paying_users || 0, icon: CreditCard, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
                { label: 'Free Users', value: stats?.free_users || 0, icon: Users, color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800' },
                { label: 'Active Now', value: stats?.active_users_now || 0, icon: Activity, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
                { label: 'Total Projects', value: stats?.total_projects || 0, icon: FolderOpen, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
                { label: 'New Complaints', value: stats?.new_complaints || 0, icon: AlertTriangle, color: stats?.new_complaints > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-400', bg: stats?.new_complaints > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-50 dark:bg-slate-800' },
                { label: 'Feedback Received', value: stats?.total_feedback || 0, icon: MessageSquare, color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
                { label: 'Active (24h)', value: stats?.active_users_24h || 0, icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20' },
              ].map((stat, i) => (
                <div key={i} className={`${stat.bg} rounded-xl p-4 border border-slate-200 dark:border-slate-700`}>
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-zenna-violet" />
                User Growth
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-white">{stats?.users_today || 0}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">New today</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-white">{stats?.users_this_week || 0}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">This week</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-white">{stats?.users_this_month || 0}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">This month</div>
                </div>
              </div>
            </div>

            {complaints.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    Recent Complaints
                  </h3>
                  <button onClick={() => setActiveTab('complaints')} className="text-xs text-zenna-violet hover:underline">
                    View all →
                  </button>
                </div>
                <div className="space-y-2">
                  {complaints.slice(0, 5).map(c => (
                    <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severityColor(c.severity)}`}>{c.severity}</span>
                      <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">{c.zenna_summary}</span>
                      <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleDateString()}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(c.status)}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Marketing Tab - NEW! */}
        {activeTab === 'marketing' && (
          <MarketingTools adminEmail={user.email} />
        )}

        {/* Social Media Tab */}
        {activeTab === 'social' && (
          <SocialMediaManager adminEmail={user.email} />
        )}

        {/* Feature Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <FeatureSuggestions adminEmail={user.email} />
        )}

        {/* Complaints Tab */}
        {activeTab === 'complaints' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filter:</span>
              {['', 'new', 'investigating', 'resolved', 'dismissed'].map(f => (
                <button
                  key={f}
                  onClick={() => { setComplaintFilter(f); setComplaintsPage(1) }}
                  className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                    complaintFilter === f 
                      ? 'bg-zenna-violet text-white' 
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {f || 'All'}
                </button>
              ))}
              <span className="ml-auto text-xs text-slate-400">{complaintsTotal} total</span>
            </div>

            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase border-b border-slate-100 dark:border-slate-700">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">User</div>
              <div className="col-span-3">Summary</div>
              <div className="col-span-1">Category</div>
              <div className="col-span-1">Severity</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Actions</div>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {complaints.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  {complaintFilter ? `No ${complaintFilter} complaints` : 'No complaints yet — your users are happy! 🎉'}
                </div>
              ) : (
                complaints.map(c => (
                  <div key={c.id}>
                    <div 
                      className="grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/30 cursor-pointer items-center"
                      onClick={() => setExpandedComplaint(expandedComplaint === c.id ? null : c.id)}
                    >
                      <div className="col-span-2 text-xs text-slate-500 dark:text-slate-400">
                        {new Date(c.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </div>
                      <div className="col-span-2 text-slate-700 dark:text-slate-300 truncate text-xs">{c.user_email}</div>
                      <div className="col-span-3 text-slate-600 dark:text-slate-400 truncate">{c.zenna_summary}</div>
                      <div className="col-span-1">
                        <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-1.5 py-0.5 rounded">{c.category}</span>
                      </div>
                      <div className="col-span-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severityColor(c.severity)}`}>{c.severity}</span>
                      </div>
                      <div className="col-span-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(c.status)}`}>{c.status}</span>
                      </div>
                      <div className="col-span-2 flex items-center gap-1">
                        {c.status === 'new' && (
                          <>
                            <button 
                              onClick={(e) => { e.stopPropagation(); updateComplaint(c.id, 'investigating') }}
                              className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded hover:bg-yellow-200"
                            >
                              Investigate
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); updateComplaint(c.id, 'dismissed') }}
                              className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded hover:bg-slate-200"
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                        {c.status === 'investigating' && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); updateComplaint(c.id, 'resolved') }}
                            className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded hover:bg-green-200"
                          >
                            Resolve
                          </button>
                        )}
                        {expandedComplaint === c.id ? (
                          <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />
                        )}
                      </div>
                    </div>

                    {expandedComplaint === c.id && (
                      <div className="px-4 py-3 bg-slate-50 dark:bg-slate-700/30 border-t border-slate-100 dark:border-slate-700">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">User's Message</h4>
                            <p className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-600">{c.user_message}</p>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Zenna's Assessment</h4>
                            <p className="text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 p-3 rounded-lg border border-slate-200 dark:border-slate-600">{c.zenna_summary}</p>
                          </div>
                        </div>
                        {c.admin_notes && (
                          <div className="mt-3">
                            <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">Admin Notes</h4>
                            <p className="text-sm text-slate-600 dark:text-slate-400">{c.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {complaintsTotal > 25 && (
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <button
                  onClick={() => setComplaintsPage(p => Math.max(1, p - 1))}
                  disabled={complaintsPage === 1}
                  className="text-sm text-zenna-violet disabled:text-slate-300 hover:underline"
                >
                  ← Previous
                </button>
                <span className="text-xs text-slate-400">Page {complaintsPage} of {Math.ceil(complaintsTotal / 25)}</span>
                <button
                  onClick={() => setComplaintsPage(p => p + 1)}
                  disabled={complaintsPage >= Math.ceil(complaintsTotal / 25)}
                  className="text-sm text-zenna-violet disabled:text-slate-300 hover:underline"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm"
                />
              </div>
              <span className="text-xs text-slate-400">{filteredUsers.length} users</span>
            </div>

            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase border-b border-slate-100 dark:border-slate-700">
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Name</div>
              <div className="col-span-1">Plan</div>
              <div className="col-span-2">Credits Left</div>
              <div className="col-span-2">Generations</div>
              <div className="col-span-2">Joined</div>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[500px] overflow-y-auto">
              {filteredUsers.map(u => (
                <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/30 items-center">
                  <div className="col-span-3 text-slate-700 dark:text-slate-300 truncate text-xs">{u.email}</div>
                  <div className="col-span-2 text-slate-600 dark:text-slate-400 truncate">{u.display_name || '—'}</div>
                  <div className="col-span-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      u.plan && u.plan !== 'free' 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                        : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {u.plan || 'free'}
                    </span>
                  </div>
                  <div className="col-span-2 text-slate-600 dark:text-slate-400">{u.credits_remaining ?? '—'}</div>
                  <div className="col-span-2 text-slate-600 dark:text-slate-400">{u.generations_used ?? 0}</div>
                  <div className="col-span-2 text-xs text-slate-400">
                    {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                Users Online Now
              </h3>
              <span className="text-xs text-slate-400">Last 10 minutes • Auto-refreshes every 30s</span>
            </div>

            {activeUsers.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No active users right now</div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {activeUsers.map((u, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{u.user_email}</span>
                    <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded">{u.current_page}</span>
                    <span className="text-xs text-slate-400">{new Date(u.last_heartbeat).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 text-center">
        <p className="text-xs text-slate-400">Zenna Admin Dashboard • ZenFlow Studios LLC • Auto-refreshes every 30 seconds</p>
      </div>
    </div>
  )
}

// =============================================================================
// SOCIAL MEDIA MANAGER (Embedded)
// =============================================================================

function SocialMediaManager({ adminEmail }) {
  const [accounts, setAccounts] = useState({})
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeSection, setActiveSection] = useState('accounts')
  const [manualPost, setManualPost] = useState({ imageUrl: '', caption: '', platforms: ['pinterest'] })
  const [posting, setPosting] = useState(false)
  const [topPosts, setTopPosts] = useState([])
  const [loadingTop, setLoadingTop] = useState(false)
  const [topLoaded, setTopLoaded] = useState(false)

  useEffect(() => {
    if (activeSection === 'top posts' && !topLoaded) {
      setLoadingTop(true)
      fetch(`${API_URL}/marketing/top-gallery?limit=10&days=30`, {
        headers: { 'x-admin-email': adminEmail }
      })
        .then(r => r.json())
        .then(data => { setTopPosts(data.posts || []); setLoadingTop(false); setTopLoaded(true) })
        .catch(() => { setLoadingTop(false); setTopLoaded(true) })
    }
  }, [activeSection])

  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [accountsRes, postsRes] = await Promise.all([
        fetch(`${API_URL}/social/accounts`),
        fetch(`${API_URL}/social/posts?limit=20`)
      ])
      
      if (accountsRes.ok) {
        const data = await accountsRes.json()
        setAccounts(data.accounts || {})
      }
      if (postsRes.ok) {
        const data = await postsRes.json()
        setPosts(data.posts || [])
      }
    } catch (err) {
      console.error('Failed to fetch social data:', err)
    }
    setLoading(false)
  }

  const connectPlatform = async (platform) => {
    try {
      const res = await fetch(`${API_URL}/social/${platform}/auth`)
      const data = await res.json()
      if (data.authUrl) {
        window.location.href = data.authUrl
      }
    } catch (err) {
      console.error(`Failed to connect ${platform}:`, err)
      alert(`Failed to connect ${platform}. Check console.`)
    }
  }

  const disconnectPlatform = async (platform) => {
    if (!confirm(`Disconnect ${platform}?`)) return
    try {
      await fetch(`${API_URL}/social/accounts/${platform}`, { method: 'DELETE' })
      fetchAll()
    } catch (err) {
      console.error(`Failed to disconnect ${platform}:`, err)
    }
  }

  const submitPost = async () => {
    if (!manualPost.imageUrl) {
      alert('Please enter an image URL')
      return
    }
    setPosting(true)
    try {
      const res = await fetch(`${API_URL}/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: manualPost.imageUrl,
          caption: manualPost.caption,
          platforms: manualPost.platforms
        })
      })
      const data = await res.json()
      alert(`Post results: ${JSON.stringify(data.results)}`)
      setManualPost({ imageUrl: '', caption: '', platforms: ['pinterest'] })
      fetchAll()
    } catch (err) {
      console.error('Post failed:', err)
      alert('Post failed. Check console.')
    }
    setPosting(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-zenna-violet/30 border-t-zenna-violet rounded-full animate-spin" />
      </div>
    )
  }

  const platforms = ['pinterest', 'facebook', 'instagram', 'twitter']
  const platformColors = {
    pinterest: 'bg-red-500',
    facebook: 'bg-blue-600',
    instagram: 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600',
    twitter: 'bg-blue-400'
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-2xl font-bold text-slate-800 dark:text-white">{posts.length}</div>
          <div className="text-sm text-slate-500">Total Posts</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-2xl font-bold text-slate-800 dark:text-white">
            {Object.values(accounts).filter(a => a?.is_active).length}
          </div>
          <div className="text-sm text-slate-500">Connected</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-2xl font-bold text-emerald-600">{posts.filter(p => p.status === 'posted').length}</div>
          <div className="text-sm text-slate-500">Successful</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-2xl font-bold text-red-500">{posts.filter(p => p.status === 'failed').length}</div>
          <div className="text-sm text-slate-500">Failed</div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2">
        {['accounts', 'post', 'calendar', 'top posts', 'history'].map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
              activeSection === section
                ? 'bg-zenna-violet text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
            }`}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Accounts Section */}
      {activeSection === 'accounts' && (
        <div className="grid grid-cols-2 gap-4">
          {platforms.map(platform => {
            const account = accounts[platform]
            const isConnected = account?.is_active
            
            return (
              <div key={platform} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full ${platformColors[platform]} flex items-center justify-center text-white font-bold`}>
                    {platform[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800 dark:text-white capitalize">{platform}</div>
                    <div className="text-xs text-slate-500">
                      {isConnected ? account.account_name : 'Not connected'}
                    </div>
                  </div>
                  <div className={`ml-auto w-3 h-3 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                </div>
                
                {isConnected ? (
                  <button
                    onClick={() => disconnectPlatform(platform)}
                    className="w-full py-2 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    onClick={() => connectPlatform(platform)}
                    className="w-full py-2 text-sm text-white bg-zenna-violet rounded-lg hover:bg-zenna-violet/90"
                  >
                    Connect
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Post Section */}
      {activeSection === 'post' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">Manual Post</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Image URL</label>
              <input
                type="text"
                value={manualPost.imageUrl}
                onChange={(e) => setManualPost(prev => ({ ...prev, imageUrl: e.target.value }))}
                placeholder="https://..."
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Caption</label>
              <textarea
                value={manualPost.caption}
                onChange={(e) => setManualPost(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Enter caption..."
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white h-24 resize-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">Platforms</label>
              <div className="flex gap-2">
                {platforms.map(platform => (
                  <button
                    key={platform}
                    onClick={() => {
                      setManualPost(prev => ({
                        ...prev,
                        platforms: prev.platforms.includes(platform)
                          ? prev.platforms.filter(p => p !== platform)
                          : [...prev.platforms, platform]
                      }))
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm capitalize ${
                      manualPost.platforms.includes(platform)
                        ? 'bg-zenna-violet text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {platform}
                  </button>
                ))}
              </div>
            </div>
            
            <button
              onClick={submitPost}
              disabled={posting || !manualPost.imageUrl}
              className="w-full py-3 bg-zenna-violet text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {posting ? 'Posting...' : 'Post Now'}
            </button>
          </div>
        </div>
      )}

      {/* Calendar Section — visual week of past + scheduled posts */}
      {activeSection === 'calendar' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <h3 className="font-semibold text-slate-800 dark:text-white mb-4">Content Calendar — Last 7 Days + Upcoming</h3>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, i) => {
              const d = new Date()
              d.setDate(d.getDate() - 6 + i)
              const dayStr = d.toISOString().slice(0, 10)
              const dayPosts = posts.filter(p => p.created_at?.startsWith(dayStr))
              const isToday = i === 6
              return (
                <div key={i} className={`rounded-lg p-2 min-h-[120px] border ${isToday ? 'border-zenna-violet bg-zenna-violet/5' : 'border-slate-200 dark:border-slate-700'}`}>
                  <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-zenna-violet' : 'text-slate-500'}`}>
                    {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  {dayPosts.length === 0 ? (
                    <div className="text-[10px] text-slate-400 italic">No posts</div>
                  ) : (
                    <div className="space-y-1">
                      {dayPosts.map(p => (
                        <div key={p.id} className="flex items-center gap-1">
                          <span className={`w-4 h-4 rounded-full ${platformColors[p.platform] || 'bg-slate-400'} flex items-center justify-center text-white text-[8px] font-bold`}>
                            {p.platform?.[0]?.toUpperCase()}
                          </span>
                          <span className={`text-[10px] ${p.status === 'posted' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {p.status === 'posted' ? '✓' : '✗'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-400">
            <span>✓ = posted</span><span>✗ = failed</span>
            <span className="ml-auto">Auto-poster runs 3×/day: 9am, 1pm, 7pm ET</span>
          </div>
        </div>
      )}

      {/* Top Gallery Posts — community content for promo reuse */}
      {activeSection === 'top posts' && (() => {
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800 dark:text-white">Top Gallery Posts (Last 30 Days)</h3>
                <p className="text-xs text-slate-500 mt-0.5">Community designs you can promote — "Look what a Zenna user made!"</p>
              </div>
            </div>
            {loadingTop ? (
              <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-zenna-violet border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : topPosts.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No gallery posts in the last 30 days</div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {topPosts.map(post => (
                  <div key={post.id} className="flex items-start gap-4 p-4">
                    <img src={post.promo_ready_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-slate-800 dark:text-white truncate">{post.title}</span>
                        {post.craft_type && <span className="text-[10px] bg-zenna-violet/10 text-zenna-violet px-1.5 py-0.5 rounded-full">{post.craft_type}</span>}
                      </div>
                      <div className="flex gap-3 text-xs text-slate-500 mb-2">
                        <span>❤️ {post.like_count || 0}</span>
                        <span>💬 {post.comment_count || 0}</span>
                        <span>👁 {post.view_count || 0}</span>
                        <span className="text-slate-400">by {post.author_name}</span>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-900 rounded p-2 text-[11px] text-slate-600 dark:text-slate-400 line-clamp-2">
                        {post.suggested_caption}
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(post.suggested_caption).then(() => alert('Caption copied!'))}
                        className="mt-2 text-[11px] text-zenna-violet hover:underline"
                      >
                        Copy suggested caption
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })()}

      {/* History Section */}
      {activeSection === 'history' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h3 className="font-semibold text-slate-800 dark:text-white">Post History</h3>
          </div>
          
          {posts.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No posts yet</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {posts.map(post => (
                <div key={post.id} className="flex items-center gap-4 p-4">
                  <img src={post.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full ${platformColors[post.platform]} flex items-center justify-center text-white text-xs font-bold`}>
                        {post.platform[0].toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-slate-800 dark:text-white capitalize">{post.platform}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        post.status === 'posted' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {post.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-1">{post.caption}</p>
                  </div>
                  <div className="text-xs text-slate-400">
                    {new Date(post.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}